import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Modal, { MODAL_SIZE } from "components/Modal";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";

/**
 * InterviewQuestionAIVideoPreviewModal
 *
 * @param {Object}     question
 * @param {Object}     task
 * @param {string}     videoSrc
 * @param {Function}   onConfirm
 * @param {Function}   onDiscard
 */
const InterviewQuestionAIVideoPreviewModal = ({ question, task, videoSrc, onConfirm, onDiscard, ...props }) => (
  <Modal
    onSubmit={onConfirm}
    onDestructive={onDiscard}
    title={<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Modal.Title" />}
    size={MODAL_SIZE.large}
    showSave={true}
    showSaveAsDestructable={false}
    isDestructable={true}
    showCancel={true}
    canSubmit={true}
    primaryButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Select"
    secondaryButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Hide"
    desctructableButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Discard.Video"
    {...props}
  >
    <div css={styles.result}>
      <div css={styles.successMessage}>
        <div css={styles.successIcon}>
          <Icon type={ICON_TYPE.checkDotted} size={ICON_SIZE.xlarge} color={colors.green} />
        </div>

        <h3 css={styles.title}>
          <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Success.Title" />
        </h3>

        <p css={styles.description}>
          <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Success.Description" />
        </p>
      </div>

      <div css={styles.previewCard}>
        <div css={styles.previewMedia}>
          {videoSrc ? (
            <video controls css={styles.video} poster={task?.thumbnailUrl || undefined}>
              <source src={videoSrc} type="video/mp4" />
            </video>
          ) : null}
        </div>

        <div css={styles.questionPanel}>
          {question?.title && <div css={styles.questionTitle}>{question.title}</div>}
          {question?.description && <div css={styles.questionDescription}>{question.description}</div>}
        </div>
      </div>
    </div>
  </Modal>
);

const styles = {
  result: css`
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    padding: 1rem 0;
  `,
  successMessage: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    text-align: center;
  `,
  successIcon: css`
    margin-top: 0.4rem;
  `,
  title: css`
    font-size: ${fontSize.large};
    font-weight: 600;
    color: ${colors.grayAnatomyBase};
    margin-bottom: 0.8rem;
  `,
  description: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    max-width: 50rem;
    line-height: 1.5;
    margin: 0;
  `,
  previewCard: css`
    margin-top: 2rem;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 2rem;
    align-items: stretch;
    width: 100%;
  `,
  previewMedia: css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    height: 100%;
  `,
  questionPanel: css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: 100%;
    text-align: left;
    padding: 3rem;
    height: 100%;
    justify-content: flex-start;
    background: ${colors.grayAnatomyLight5};
    border-radius: 0.4rem;
    box-sizing: border-box;
  `,
  questionTitle: css`
    font-size: ${fontSize.medium};
    font-weight: 600;
    color: ${colors.grayAnatomyBase};
    line-height: 1.4;
  `,
  questionDescription: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyLight1};
    line-height: 1.7;
    white-space: pre-wrap;
  `,
  video: css`
    width: 100%;
    height: 100%;
    min-height: 28rem;
    max-height: 28rem;
    background: #000;
    border-radius: 0.8rem;
    box-shadow: 0 0.4rem 1.6rem rgba(0, 0, 0, 0.08);
    object-fit: cover;
  `,
};

InterviewQuestionAIVideoPreviewModal.propTypes = {
  question: PropTypes.object,
  task: PropTypes.object,
  videoSrc: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};

export default InterviewQuestionAIVideoPreviewModal;
